import { prop, uniqBy } from 'ramda';
import { addContent } from 'utils/apiWrapper';
import { loadFont } from 'utils/textTool';
import openType from 'opentype.js';
import { API_URL } from 'utils/env';
import axios from 'axios';
import { getCanvasBlob } from 'utils/utils';
import { IRgb } from '@picsart/rc/types/components/ColorPicker/types';
import { authorizationHeader } from '@picsart/growth-rc/services/auth';

const NAME_KEEPING_TEXT_FONTS = 've_FontNames';
const KEEP_FONTS_COUNT = 50;

type TextData = {
  font_family: string;
  font_preview: string;
  font_src: string;
  isPremium?: boolean;
};

type SaveFont = {
  fontUrl: string;
  previewUrl: string;
  fontFamily: string;
  userKey: string;
};

export const getFilesMeta = async (file: File) => {
  const {
    names: {
      fontFamily: { en: fontFamily }
    }
  } = openType.parse(await file.arrayBuffer());
  return { fontFamily, file };
};

export function hexToRgb(hex: string) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (_, r, g, b) => {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
}

function componentToHex(c: number) {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}

export function rgbToHex(rgb: IRgb) {
  const { r, g, b } = rgb;
  return `${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

const blobToFile = (theBlob: Blob, fileName: string): File => {
  const b: any = theBlob;
  b.lastModifiedDate = new Date();
  b.name = fileName;

  // Cast to a File() type
  return <File>theBlob;
};

export const makeFontPreviewPng = async (src: string, fontFamily: string): Promise<Blob | null> => {
  const canvas = document.createElement('canvas');
  canvas.width = 260;
  canvas.height = 40;
  const ctx = canvas.getContext('2d');
  await loadFont({ fontFamily, src });
  if (!ctx) {
    return null;
  }
  ctx.font = `30px ${fontFamily}`;
  ctx.fillText(fontFamily, 10, 30);
  return getCanvasBlob(canvas, 'image/png', 1);
};

export const getKeptFontsStorage = (userKey: string) => {
  const getFontsLocalStorage = window.localStorage.getItem(`${NAME_KEEPING_TEXT_FONTS}_${userKey}`);
  if (!getFontsLocalStorage) {
    return;
  }

  let fontsArr;

  try {
    fontsArr = JSON.parse(getFontsLocalStorage);
  } catch (e) {
    return;
  }

  return fontsArr;
};

export const keepDataOnLocalStorage = (dataName: string, currentData: TextData[]) => {
  window.localStorage.setItem(dataName, JSON.stringify(currentData));
};

export const moveFontToTop = (fontList: TextData[], currentFont: TextData) => {
  const duplicateFontIndex = fontList.findIndex(
    (el: TextData) => el.font_family === currentFont.font_family
  );

  if (duplicateFontIndex >= 0) {
    fontList.splice(duplicateFontIndex, 1);
  }
  fontList.unshift(currentFont);
};

export const keepFontsStorage = (currentFont: TextData | null, userKey: string) => {
  if (!currentFont || !userKey) {
    return;
  }

  const getFontsLocalStorage = getKeptFontsStorage(userKey);
  const keptFontList = getFontsLocalStorage || [currentFont];

  if (!getFontsLocalStorage) {
    keepDataOnLocalStorage(`${NAME_KEEPING_TEXT_FONTS}_${userKey}`, [currentFont]);
    return;
  }

  moveFontToTop(keptFontList, currentFont);

  if (keptFontList.length > KEEP_FONTS_COUNT) {
    keptFontList.pop();
  }
  keepDataOnLocalStorage(`${NAME_KEEPING_TEXT_FONTS}_${userKey}`, keptFontList);
};

export const getRecentlyUsedFonts = (userKey: string) => {
  const fontsArr = getKeptFontsStorage(userKey);
  if (!fontsArr) {
    return;
  }
  return fontsArr;
};

const saveFont = async ({ fontUrl, fontFamily, previewUrl, userKey }: SaveFont) => {
  const data = new FormData();
  data.append('item_url', fontUrl);
  data.append('title', fontFamily);
  data.append('preview_url', previewUrl);
  if (!userKey) {
    return;
  }
  const res = await axios({
    method: 'post',
    url: `${API_URL}/collections/items/font`,
    data,
    headers: { ...authorizationHeader(userKey) }
  });

  keepFontsStorage(
    { font_family: fontFamily, font_preview: previewUrl, font_src: fontUrl, isPremium: false },
    userKey
  );
  return res.data.status === 'success';
};

const uploadFont = async (file: File, fontFamily: string, userKey: string) => {
  const fontUrl: string = await addContent(file, userKey);
  const previewPng: Blob = await makeFontPreviewPng(fontUrl, fontFamily);
  const previewUrl = await addContent(blobToFile(previewPng, `${fontFamily}.png`), userKey);
  return saveFont({
    fontUrl,
    previewUrl,
    fontFamily,
    userKey
  });
};

export const uploadFonts = async (files: File[], currentFonts: string[], userKey: string) => {
  const parsedFiles = await Promise.all(files.map(file => getFilesMeta(file)));
  const uniqFonts = uniqBy(prop('fontFamily'), parsedFiles).filter(
    ({ fontFamily }) => !currentFonts.includes(fontFamily)
  );

  return Promise.all(
    uniqFonts.map(({ file, fontFamily }) => uploadFont(file, fontFamily, userKey))
  );
};

export const getFontFamily = async (url: string) => {
  const { data: font } = await axios.get(url, {
    responseType: 'blob'
  });

  const fontFile = new File([font], '');
  return getFilesMeta(fontFile);
};
